/**
 * PageLockMap tracks which pages are locked and their aliases.
 * It lives inside PaginationPluginState and is rebuilt from scratch on each
 * pagination cycle via PageLockMap.from().
 */

import { Node as PmNode } from 'prosemirror-model'

import { constants } from '@showrunner/prose-schemas'

import {
  getLockedPageNumber,
  isLockedPageBreak,
} from '#util/pagination/helpers'
import { deriveAliases } from '#util/pagination/PaginationBreakdown'

import { BasePage } from './Paginator'

const { NodeTypeMap } = constants

export interface LockedPageEntry {
  // Doc position of the first block — pass directly to setNodeAttribute
  startPos: number
  // 1-based sequential page index
  pageNumber: number
  // Display/lock alias, e.g. "2A" for inserted pages between locks
  alias?: string
  // True when the first block of a locked page has no text content
  isEmpty: boolean
  // is the page locked (i.e. starts with a locked page break at a block boundary)
  isLocked: boolean
}

export class PageLockMap {
  private constructor(readonly entries: LockedPageEntry[]) {}

  static from(pages: BasePage[], doc?: PmNode): PageLockMap {
    const entries = pages.map((page, i) => pageToEntry(doc, page, i))
    return new PageLockMap(
      hasLockedEntries(entries)
        ? derivePageAliases(entries)
        : resetUnlockedAliases(entries),
    )
  }

  get lockedPages(): LockedPageEntry[] {
    return this.entries.filter((entry) => entry.isLocked)
  }

  getEntry(pageNumber: number): LockedPageEntry | undefined {
    return this.entries[pageNumber - 1]
  }
}

function pageToEntry(
  doc: PmNode | undefined,
  page: BasePage,
  index: number,
): LockedPageEntry {
  const { startBlock } = page
  const startPos = resolveStartBlockPos(doc, page)
  const startsAtBlockBoundary = startPos === page.start
  const isLocked = startsAtBlockBoundary && isLockedPageBreak(startBlock)
  const isEmpty = isEmptyPage(page)
  return {
    pageNumber: index + 1,
    startPos,
    isLocked,
    alias: getLockedPageNumber(startBlock),
    isEmpty,
  }
}

function isEmptyPage(page: BasePage): boolean {
  return (
    page.startBlock === page.endBlock &&
    page.startBlock.textContent.trim() === ''
  )
}

function resolveStartBlockPos(doc: PmNode | undefined, page: BasePage): number {
  if (!doc) return page.start
  const $start = doc.resolve(page.start)

  if (
    ($start.parent.type.name === NodeTypeMap.PAGE ||
      $start.parent.type.name === NodeTypeMap.TITLE_PAGE) &&
    $start.nodeAfter
  ) {
    return page.start
  }

  for (let depth = $start.depth; depth >= 0; depth--) {
    const parent = depth > 0 ? $start.node(depth - 1) : null
    if (
      parent &&
      (parent.type.name === NodeTypeMap.PAGE ||
        parent.type.name === NodeTypeMap.TITLE_PAGE)
    ) {
      return $start.before(depth)
    }
  }

  return page.start
}

function derivePageAliases(entries: LockedPageEntry[]): LockedPageEntry[] {
  return deriveAliases(
    entries.map((entry) => ({
      ...entry,
      isLockedPageBreak: entry.isLocked,
      pageAlias: entry.isLocked ? entry.alias : undefined,
    })),
  ).map(({ isLockedPageBreak, pageAlias, ...entry }) => ({
    ...entry,
    alias: pageAlias,
  }))
}

function resetUnlockedAliases(entries: LockedPageEntry[]): LockedPageEntry[] {
  return entries.map((entry) =>
    entry.isLocked ? entry : { ...entry, alias: undefined },
  )
}

function hasLockedEntries(entries: LockedPageEntry[]): boolean {
  return entries.some((entry) => entry.isLocked)
}
